import React, {
  Fragment,
  useReducer,
  useState,
  useEffect,
  useRef,
} from 'react';
import axios from 'axios';
import { cloneDeep, get, has, isEmpty } from 'lodash';
import { withRouter } from 'react-router';
import { withCourseOutline } from 'chameleon-packages/units';
import { withRedux } from 'chameleon/legacy/src/components/assessment/Results/RelatedData/RelatedVideos/wrappers/';
import { connect } from 'react-redux';
import { compose, graphql } from 'react-apollo';
import { LoadingSpinner } from 'legacy-packages/ft-cb';
import createReducer from 'legacy/src/shared/util/createReducer';
import log from 'chameleon/ui-stack/utilities/log';
import { getUserEducationPeriods } from 'legacy-src/queries';
import {
  ACTIONS,
  TYPES,
  INITIAL_DISTRICTS,
  INITIAL_SCHOOLS,
  INITIAL_SECTIONS,
  INITIAL_STATUS,
  INITIAL_STUDENTS,
  ALL_SCHOOLS,
  filterHierarchyData,
  defaultValues,
  formatUserEducationPeriods,
  formatCurrentEducationPeriod,
  setInitialGroupFilterState,
  applyMiddleware,
  setData,
  prefix_object,
} from './utils';

import { setActiveEducationPeriod as storeActiveEdPeriod } from './actions';

/**
 * Component to provide selected group information to consuming components.
 * This component is responsible for:
 * - fetching the hierarchy from service-reports
 * - providing functions to consuming components to change the selected group
 * - providing overall hierarchy, group lists, and student list
 * @param {boolean} disableFetch disable hierarchy BE fetch, used for usage tab
 * @param {boolean} hideStudents hide the students dropdown, including the all students value in sections dropdown
 * @param {boolean} hideAllStudents hide the all students value in the sections dropdown
 * @param {boolean} disableAllSections hide the all sections value in the sections dropdown, use the first section as default
 */
const FilterControls = ({
  children,
  disableFetch = false,
  endpoint = `${FYM_BACKEND_REPORTING_URL}/api/hierarchy`,
  hideStudents = false,
  hideAllStudents = false,
  hideAllDistricts = false,
  disableAllSections = false,
  hierarchyData = {},
  currentEducationPeriod,
  showEducationPeriodFilter,
  storeActiveEducationPeriod,
  location,
  params,
  router,
  isTeacher,
  showEmptySelection,
  data: { loading: isLoading, userEducationPeriods },
  courseOutline,
}) => {
  const earliestReportingEp = courseOutline?.earliestReportingEp || 0;
  const ongoingEducationPeriod = formatCurrentEducationPeriod(
    currentEducationPeriod,
  );
  const defaultSection = disableAllSections
    ? []
    : INITIAL_SECTIONS.defaultValue;
  // filter out the all students value depending on props
  let initSections = INITIAL_SECTIONS.values.filter((section) =>
    hideStudents || hideAllStudents
      ? defaultValues.allStudents.value !== section.value
      : true,
  );
  // filer out the all sections value depending on props.
  initSections = initSections.filter((section) =>
    disableAllSections
      ? defaultValues.allSections.value !== section.value
      : true,
  );
  const initialState = {
    activeDistrict: INITIAL_DISTRICTS.defaultValue,
    activeSection: defaultSection,
    activeStudent: INITIAL_STUDENTS.defaultValue,
    activeEducationPeriod: ongoingEducationPeriod.defaultValue,
    activeUserStatus: INITIAL_STATUS.defaultValue,
    districts: INITIAL_DISTRICTS.values,
    schools: INITIAL_SCHOOLS.values.filter(
      (school) => !(isTeacher && school === ALL_SCHOOLS),
    ),
    sections: initSections,
    students: INITIAL_STUDENTS.values,
    allEducationPeriods: isLoading
      ? []
      : formatUserEducationPeriods(userEducationPeriods, earliestReportingEp),
    hierarchy: {},
    showCachedFilterProps: false,
    flattenedGroups: {},
  };

  // make a copy of the initialState
  const clonedFilterProps = cloneDeep(initialState);

  const reducer = createReducer(initialState, {
    [ACTIONS.handleActiveDistrict]: (state, { payload }) => ({
      ...state,
      activeDistrict: payload,
    }),
    [ACTIONS.handleActiveSection]: (state, { payload }) => ({
      ...state,
      activeSection: payload,
    }),
    [ACTIONS.handleActiveStudent]: (state, { payload }) => ({
      ...state,
      activeStudent: payload,
    }),
    [ACTIONS.handleActiveEducationPeriod]: (state, { payload }) => ({
      ...state,
      activeEducationPeriod: payload,
    }),
    [ACTIONS.handleActiveUserStatus]: (state, { payload }) => ({
      ...state,
      activeUserStatus: payload,
    }),
    [ACTIONS.setDistricts]: (state, { payload }) => ({
      ...state,
      districts: payload,
    }),
    [ACTIONS.setSchools]: (state, { payload }) => ({
      ...state,
      schools: payload,
    }),
    [ACTIONS.setSections]: (state, { payload }) => ({
      ...state,
      sections: payload,
    }),
    [ACTIONS.setStudents]: (state, { payload }) => ({
      ...state,
      students: payload,
    }),
    [ACTIONS.setAllEducationPeriods]: (state, { payload }) => ({
      ...state,
      allEducationPeriods: payload,
    }),
    [ACTIONS.handleSetupInitialState]: (state, { payload }) => ({
      ...state,
      ...payload,
      cachedFilterProps: {
        ...state.cachedFilterProps,
        ...payload,
      },
    }),
    [ACTIONS.updateCachedFilterProps]: (state, { payload }) => ({
      ...state,
      cachedFilterProps: payload,
    }),
    [ACTIONS.toggleCachedFilterProps]: (state, { payload }) => ({
      ...state,
      showCachedFilterProps: payload,
    }),
    [ACTIONS.storeFlattenedGroups]: (state, { payload }) => ({
      ...state,
      flattenedGroups: payload,
    }),
  });

  const defaultState = {
    ...initialState,
    cachedFilterProps: clonedFilterProps,
  };
  const [state, dispatchFnc] = useReducer(reducer, defaultState);

  useEffect(() => {
    !isLoading &&
      userEducationPeriods &&
      dispatch({
        type: ACTIONS.setAllEducationPeriods,
        payload: initialState.allEducationPeriods,
      });
  }, [userEducationPeriods, isLoading]);

  const {
    hierarchy,
    districts,
    schools,
    sections,
    students,
    allEducationPeriods,
    activeDistrict,
    activeSection,
    activeStudent,
    activeEducationPeriod,
    activeUserStatus,
    cachedFilterProps,
    showCachedFilterProps,
  } = state;
  const dispatch = applyMiddleware({
    dispatch: dispatchFnc,
    state,
    location,
    router,
  });

  const [isHierarchyLoaded, setHasLoadedHierarchy] = useState(false);
  const [isInitialStateLoaded, setIsInitialStateLoaded] = useState(false);

  // District handlers
  const setActiveDistrict = (payload) =>
    dispatch({ type: ACTIONS.handleActiveDistrict, payload });
  // Section handlers
  const setActiveSection = (payload) =>
    dispatch({ type: ACTIONS.handleActiveSection, payload });
  // userStatus handlers
  const setActiveUserStatus = (payload) =>
    dispatch({ type: ACTIONS.handleActiveUserStatus, payload });
  // Student handlers
  const setActiveStudent = !hideStudents
    ? (payload) => dispatch({ type: ACTIONS.handleActiveStudent, payload })
    : () => {};
  // Education Period Handlers
  const setActiveEducationPeriod = (payload) => {
    storeActiveEducationPeriod(payload.value);
    dispatch({ type: ACTIONS.handleActiveEducationPeriod, payload });
  };

  const listOfKeysToCheck = [
    'districtId',
    'schoolId',
    'sectionId',
    'studentId',
    'userStatus',
    'educationPeriod',
  ];
  // check if query params is setting initial state
  // ref is INSTEAD of useState to prevent re-render when changing this value after first render
  const hasQueryParams = useRef(
    !isEmpty(location.search) &&
      listOfKeysToCheck.some((key) => key in location.query, false),
  );
  const useLocalStorage = useRef(
    !hasQueryParams.current &&
      !isEmpty(
        JSON.parse(
          window.localStorage.getItem(TYPES.localStorageGroupFilterKey),
        ),
      ),
  );

  const resetSection = () => {
    setActiveSection(INITIAL_SECTIONS.defaultValue);
    dispatch({ type: ACTIONS.setSections, payload: initialState.sections });
  };
  const resetSectionWithSectionSelection = (section) => {
    setActiveSection(section);
    dispatch({ type: ACTIONS.setSections, payload: initialState.sections });
  };
  const resetStudent = () => {
    setActiveStudent(INITIAL_STUDENTS.defaultValue);
    dispatch({ type: ACTIONS.setStudents, payload: initialState.students });
  };
  const resetDistrict = () => {
    setActiveDistrict(INITIAL_DISTRICTS.defaultValue);
    dispatch({ type: ACTIONS.setDistricts, payload: initialState.districts });
  };

  const handleHierarchyData = ({ data = {} }) => {
    // remove schools without sections and districts without schools
    const filteredData = filterHierarchyData(data);
    const groupFiltersFromLocalStorage = JSON.parse(
      window.localStorage.getItem(TYPES.localStorageGroupFilterKey),
    );

    const flattenedHierarchy = Object.values(filteredData).reduce(
      (acc, district) => [
        ...acc,
        ...Object.values(district.schools).reduce(
          (acc2, school) => [
            ...acc2,
            ...Object.values(school.sections).map((section) => {
              const obj = {
                ...prefix_object('district_', district),
                ...prefix_object('school_', school),
                ...prefix_object('section_', section),
              };
              delete obj.section_students;
              delete obj.district_schools;
              delete obj.school_sections;
              return obj;
            }),
          ],
          [],
        ),
      ],
      [],
    );

    // if using query params
    const initialGroupFilterState =
      hasQueryParams.current || (disableFetch && !isEmpty(location.search))
        ? {
            activeDistrictId: location.query.districtId,
            activeSectionId: location.query.sectionId,
            activeStudentId: location.query.studentId,
            activeSchoolId: location.query.schoolId,
            activeUserStatusValue: location.query.userStatus,
            activeEducationPeriodValue: location.query.educationPeriod,
            // if using local storage
          }
        : useLocalStorage.current
          ? {
              activeDistrictId: groupFiltersFromLocalStorage.districtId,
              activeSectionId: groupFiltersFromLocalStorage.sectionId,
              activeUserStatusValue: groupFiltersFromLocalStorage.userStatus,
              activeStudentId: groupFiltersFromLocalStorage.studentId,
              activeSchoolId: groupFiltersFromLocalStorage.schoolId,
              activeEducationPeriodValue:
                groupFiltersFromLocalStorage.educationPeriod,
            }
          : {
              activeDistrictId: INITIAL_DISTRICTS.defaultValue.value,
              activeSectionId: INITIAL_SECTIONS.defaultValue.value,
              activeStudentId: INITIAL_STUDENTS.defaultValue.value,
              activeSchoolId: INITIAL_SCHOOLS.defaultValue.value,
              activeUserStatusValue: null,
              activeEducationPeriodValue: null,
            };

    const flattenedSchoolsObject = Object.values(filteredData).reduce(
      (schoolsAccum, district) => ({
        ...schoolsAccum,
        ...Object.values(district.schools || {}).reduce(
          (schoolAccum, school) => ({
            ...schoolAccum,
            [school.id]: {
              ...school,
              parentId: district.id,
              type: TYPES.school,
            },
          }),
          {},
        ),
      }),
      {},
    );

    const flattenedSectionsObject = Object.values(
      flattenedSchoolsObject,
    ).reduce(
      (sectionsAccum, school) => ({
        ...sectionsAccum,
        ...Object.values(school.sections || {}).reduce(
          (sectionAccum, section) => ({
            ...sectionAccum,
            [section.id]: {
              ...section,
              parentId: school.id,
              type: TYPES.section,
            },
          }),
          {},
        ),
      }),
      {},
    );

    const flattenedStudentsObject = Object.values(
      flattenedSectionsObject,
    ).reduce(
      (studentsAccum, section) => ({
        ...studentsAccum,
        ...Object.values(section.students || {}).reduce(
          (studentAccum, student) => ({
            ...studentAccum,
            [student.id]: {
              ...student,
              parentId: section.id,
              type: TYPES.student,
            },
          }),
          {},
        ),
      }),
      {},
    );

    dispatch({
      type: ACTIONS.storeFlattenedGroups,
      payload: {
        ...flattenedStudentsObject,
        ...flattenedSchoolsObject,
        ...flattenedSectionsObject,
        ...filteredData,
      },
    });

    // try to resolve activeIds,
    // precedent goes:
    // 1. explicit value from parameters/storage
    // 2. grab the parent id from flattened lsit
    // 3. fall back to value from parameters/storage (might just be default values)
    const resolvedActiveSectionId =
      initialGroupFilterState.activeSectionId > 0
        ? initialGroupFilterState.activeSectionId
        : get(
            flattenedStudentsObject,
            [initialGroupFilterState.activeStudentId, 'parentId'],
            initialGroupFilterState.activeSectionId,
          );

    const resolvedActiveSchoolId =
      initialGroupFilterState.activeSchoolId > 0
        ? initialGroupFilterState.activeSchoolId
        : get(
            flattenedStudentsObject,
            [initialGroupFilterState.activeSectionId, 'parentId'],
            initialGroupFilterState.activeSchoolId,
          );

    let flattenedRow = flattenedHierarchy.find(
      (val) =>
        val.section_id.toString() === resolvedActiveSectionId ||
        val.school_id.toString() === resolvedActiveSchoolId,
    );

    const resolvedActiveDistrictId =
      initialGroupFilterState.activeDistrictId > 0
        ? initialGroupFilterState.activeDistrictId
        : flattenedRow
          ? flattenedRow.district_id
          : initialGroupFilterState.activeDistrictId;

    const resolvedInitialFilterState = {
      ...initialGroupFilterState,
      activeDistrictId: resolvedActiveDistrictId,
      activeSectionId: resolvedActiveSectionId,
    };

    setInitialGroupFilterState({
      dispatch,
      filteredData,
      userEducationPeriods,
      initialState,
      hasInitialStateFromStorageOrQuery:
        hasQueryParams.current ||
        (disableFetch && !isEmpty(location.search)) ||
        useLocalStorage.current,
      showEmptySelection,
      hideAllDistricts,
      ...resolvedInitialFilterState,
    });
  };

  // districts effect, responds to hierarchy change
  useEffect(() => {
    if (isHierarchyLoaded && isInitialStateLoaded) {
      // only run when hierarchy is not empty
      if (!isEmpty(hierarchy)) {
        dispatch({
          type: ACTIONS.setDistricts,
          payload: setData({
            data: hierarchy,
            initialState: initialState.districts,
            type: TYPES.district,
          }).aggregatedList,
        });
      } else {
        resetDistrict();
      }
    }
  }, [activeEducationPeriod.value]);

  // sections effect, responds to change in active school
  useEffect(() => {
    if (isHierarchyLoaded && isInitialStateLoaded) {
      if (!isEmpty(hierarchy)) {
        // get sections from hierarchy -> active district -> active school -> sections
        const districtSchools = [
          ...Object.values(get(hierarchy, [activeDistrict.value, 'schools'])),
        ];
        const sections = districtSchools.reduce(
          (acc, school) => [...acc, ...Object.values(school.sections)],
          [],
        );
        if (!isEmpty(sections)) {
          const sectionsList = setData({
            data: sections,
            initialState: initialState.sections,
            type: TYPES.section,
          }).aggregatedList;
          if (
            !sectionsList.some(
              (section) => section.value === activeSection.value,
            )
          ) {
            resetSectionWithSectionSelection(sectionsList[0]);
          }
          dispatch({
            type: ACTIONS.setSections,
            payload: sectionsList,
          });
        } else {
          resetSection();
        }
      } else {
        resetSection();
      }
    }
  }, [hierarchy, activeEducationPeriod.value, activeDistrict.value]);

  // students effect, responds to change in active section
  useEffect(() => {
    if (isHierarchyLoaded && isInitialStateLoaded) {
      if (!hideStudents) {
        // active section selection is one of the initial section values
        const activeSectionIsDefault = INITIAL_SECTIONS.values.some(
          (section) => section.value === activeSection.value,
        );

        // all sections in district
        const getSections = Object.values(
          get(hierarchy, [activeDistrict.value, 'schools'], {}),
        ).reduce((acc, school) => ({ ...acc, ...school.sections }), {});

        // student data will contain all students from that school
        const getStudents = activeSectionIsDefault
          ? // go through all the sections and accumulate the students list from each section into a single list
            Object.values(getSections).reduce((prev, curr) => {
              // append the section_id to the student_id
              const studentWithSectionId = Object.values(curr.students).map(
                (student) => ({
                  ...student,
                  id: `${curr.id}-${student.id}`,
                }),
              );
              return has(curr, 'students')
                ? [...prev, ...studentWithSectionId]
                : prev;
            }, [])
          : // otherwise just grab the students in the selected section: getSections -> active section -> students
            // append the section_id to the student_id
            Object.values(
              get(getSections, [activeSection.value, 'students'], {}),
            ).map((student) => ({
              ...student,
              id: `${activeSection.value}-${student.id}`,
            }));
        if (!isEmpty(getStudents)) {
          if (
            activeStudent.value.split &&
            get(activeStudent.value.split('-'), 0) !==
              activeSection.value.toString()
          ) {
            resetStudent();
          }
          dispatch({
            type: ACTIONS.setStudents,
            payload: setData({
              data: getStudents,
              initialState: initialState.students,
              type: TYPES.student,
            }).aggregatedList,
          });
        } else {
          resetStudent();
        }
      } else {
        resetStudent();
      }
    }
  }, [
    hierarchy,
    activeEducationPeriod.value,
    activeDistrict.value,
    activeSection.value,
    isInitialStateLoaded,
  ]);

  /**
   * initial fetch for hierarchy from service-reports
   *** setHierarchy with returned data
   */
  useEffect(() => {
    const groupFiltersFromLocalStorage = JSON.parse(
      window.localStorage.getItem(TYPES.localStorageGroupFilterKey),
    );
    const educationPeriod = showEducationPeriodFilter
      ? hasQueryParams.current && !isEmpty(location.query.educationPeriod)
        ? location.query.educationPeriod
        : useLocalStorage.current
          ? groupFiltersFromLocalStorage.educationPeriod
          : activeEducationPeriod.value
      : null;
    // syncing state from query param/local storage, do not fetch
    if (!disableFetch && allEducationPeriods) {
      let newActiveEducationPeriod = allEducationPeriods.find(
        (ep) => parseInt(ep.value, 10) === parseInt(educationPeriod, 10),
      );
      const skipFetchData =
        activeEducationPeriod.value !== educationPeriod &&
        newActiveEducationPeriod != null;

      // if we try to use the localStorage value but it isn't defined for this subject, lets try to use the current val
      // or the most recent year prior
      if (newActiveEducationPeriod == null && !isInitialStateLoaded) {
        // work back from now and find the newest year
        const periods = [...allEducationPeriods].filter(
          (ele) => ele.value <= currentEducationPeriod,
        );
        periods.sort((ele, next) => ele.value < next.value);
        [newActiveEducationPeriod] = periods;
      }
      newActiveEducationPeriod &&
        setActiveEducationPeriod(newActiveEducationPeriod);
      if (!skipFetchData && newActiveEducationPeriod) {
        axios
          .get(endpoint, {
            requestId: 'reports-hierarchy',
            params: {
              subject_id: params.subject,
              education_period: newActiveEducationPeriod.value,
            },
          })
          .then((data) => {
            setHasLoadedHierarchy(true);
            handleHierarchyData(data);
            setIsInitialStateLoaded(true);
          })
          .catch((err) => log(err));
      }
    } else {
      setHasLoadedHierarchy(true);
      handleHierarchyData({ data: hierarchyData });
      if (disableFetch) {
        setIsInitialStateLoaded(true);
      }
    }
  }, [
    activeEducationPeriod.value,
    allEducationPeriods,
    disableFetch && hierarchyData,
  ]);

  const filterProps = {
    activeDistrict,
    activeSection,
    activeUserStatus,
    activeStudent,
    activeEducationPeriod,
    hierarchy,
    districts,
    schools,
    sections,
    students,
    allEducationPeriods,
    setActiveDistrict,
    setActiveSection,
    setActiveUserStatus,
    setActiveStudent,
    setActiveEducationPeriod,
    hideStudents,
    showEducationPeriodFilter,
    isInitialStateLoaded,
  };

  // storedFilterProps is intended to reflect the most updated state of the FilterControls
  // with the intention is that storedFilterProps will be used to render the Filters dropdowns

  // filterProps provides the props used to render data-informed components
  // - currently, we do not have a way to render "All Districts" because of performance concerns,
  //  so we cache the current selections and cached filter props to the children

  // if this changes in the future, we can just pass in storedFilterProps
  const renderedProps = showCachedFilterProps
    ? {
        filterProps: {
          ...filterProps,
          ...cachedFilterProps,
        },
        showCachedFilterProps,
        storedFilterProps: filterProps,
      }
    : {
        filterProps,
        showCachedFilterProps,
        storedFilterProps: filterProps,
      };

  return (
    <Fragment>
      {!isHierarchyLoaded ? (
        <LoadingSpinner />
      ) : !isInitialStateLoaded ? (
        <LoadingSpinner />
      ) : (
        children(renderedProps)
      )}
    </Fragment>
  );
};

const mapStateToProps = ({
  userData: {
    educationPeriod,
    userEducationPeriods,
    isTeacher,
    isAdmin,
    isCoordinator,
  },
  featureFlag,
}) => ({
  userEducationPeriods,
  currentEducationPeriod: educationPeriod.currentEducationPeriod,
  showEducationPeriodFilter: featureFlag.show_education_period_filter,
  isTeacher,
  isAdminOrCoordinator: isAdmin || isCoordinator,
});

export default withRouter(
  compose(
    withRedux,
    withCourseOutline(),
    graphql(getUserEducationPeriods, {
      options: (props) => {
        const { subject } = props.params || {};
        return {
          notifyOnNetworkStatusChange: true,
          fetchPolicy: 'network-only',
          variables: { subjectId: subject },
        };
      },
    }),
    connect(mapStateToProps, {
      storeActiveEducationPeriod: storeActiveEdPeriod,
    }),
  )(FilterControls),
);
